import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Typography, IconButton, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import toast from 'react-hot-toast';
import { useTheme as useAppTheme } from 'context/ThemeContext';
import { FOOT_POINTS_COUNT, FootMeshData, FootSide, useFootMeshStore } from 'context/FootMeshContext';
import { FOOT_POINT_LABELS } from 'Utils/FootMeshUtils';
import FootPointPickerScene, { FOOT_POINT_COLORS } from './FootPointPickerScene';

interface FootMeshPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Display order: left foot on the left side of the panel, right foot on the right.
const SIDES: FootSide[] = ['left', 'right'];

// A foot is roughly 100-400 mm long, i.e. 0.1-0.4 in meters. A model whose largest
// dimension is under 1 unit was saved in meters, so it is converted to mm to match the insoles.
const METERS_MAX_FOOT_SIZE = 1;
const METERS_TO_MM = 1000;

const convertMetersToMm = (geometry: THREE.BufferGeometry) => {
  geometry.computeBoundingBox();
  const size = geometry.boundingBox!.getSize(new THREE.Vector3());
  if (Math.max(size.x, size.y, size.z) < METERS_MAX_FOOT_SIZE) {
    geometry.scale(METERS_TO_MM, METERS_TO_MM, METERS_TO_MM);
    geometry.computeBoundingBox();
  }
};

const loadFootGeometry = async (file: File): Promise<THREE.BufferGeometry> => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'stl') {
    const geometry = new STLLoader().parse(await file.arrayBuffer());
    convertMetersToMm(geometry);
    geometry.computeVertexNormals();
    return geometry;
  }
  if (extension === 'obj') {
    const group = new OBJLoader().parse(await file.text());
    group.updateMatrixWorld(true);
    const geometries: THREE.BufferGeometry[] = [];
    group.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      // Keep only positions so parts with different attribute sets can be merged.
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', child.geometry.getAttribute('position').clone());
      if (child.geometry.index) geometry.setIndex(child.geometry.index.clone());
      geometry.applyMatrix4(child.matrixWorld);
      geometries.push(geometry.index ? geometry.toNonIndexed() : geometry);
    });
    if (geometries.length === 0) throw new Error('OBJ file has no meshes');
    const merged = geometries.length === 1 ? geometries[0] : mergeGeometries(geometries);
    if (!merged) throw new Error('Failed to merge OBJ meshes');
    convertMetersToMm(merged);
    merged.computeVertexNormals();
    return merged;
  }
  throw new Error('Unsupported file type');
};

const FootMeshPanel: React.FC<FootMeshPanelProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useAppTheme();
  const [feet, setFeet] = useState<Record<FootSide, FootMeshData | null>>({ right: null, left: null });
  const fileInputRefs = useRef<Record<FootSide, HTMLInputElement | null>>({ right: null, left: null });

  // Start each session from the stored feet so the user can edit previous picks.
  useEffect(() => {
    if (!isOpen) return;
    const { rightFoot, leftFoot } = useFootMeshStore.getState();
    setFeet({
      right: rightFoot && { ...rightFoot, points: rightFoot.points.map(p => p.clone()) },
      left: leftFoot && { ...leftFoot, points: leftFoot.points.map(p => p.clone()) },
    });
  }, [isOpen]);

  const updateFoot = (side: FootSide, foot: FootMeshData | null) => {
    setFeet(prev => ({ ...prev, [side]: foot }));
  };

  // Picking points on a saved (placed) foot re-aligns it from the new points.
  const updatePoints = (side: FootSide, foot: FootMeshData, points: THREE.Vector3[]) => {
    updateFoot(side, { ...foot, points, placed: false });
  };

  const handleFileChange = async (side: FootSide, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const geometry = await loadFootGeometry(file);
      updateFoot(side, { fileName: file.name, geometry, points: [] });
    } catch (err) {
      console.error(err);
      toast.error(`Could not load ${file.name}. Please select a valid OBJ or STL file.`);
    }
  };

  const handleApply = () => {
    // Saved feet keep their placement and need no points.
    const incomplete = SIDES.filter(side => feet[side] && !feet[side]!.placed && feet[side]!.points.length !== FOOT_POINTS_COUNT);
    if (incomplete.length > 0) {
      toast.error(`Select ${FOOT_POINTS_COUNT} points on the ${incomplete.join(' and ')} foot.`);
      return;
    }
    useFootMeshStore.getState().applyFeet(feet.right, feet.left);
    onClose();
  };

  const textColor = isDarkMode ? '#fff' : '#000';

  const renderFootSection = (side: FootSide) => {
    const foot = feet[side];
    const label = side === 'right' ? 'Right Foot' : 'Left Foot';
    return (
      <Box key={side} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDarkMode ? '#fff' : '#076596' }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRefs.current[side]?.click()}
              sx={{ color: textColor, borderColor: '#aaa' }}
            >
              {foot ? 'Replace' : 'Import'}
            </Button>
            <Tooltip title={`Remove ${label.toLowerCase()}`}>
              <span>
                <IconButton size="small" disabled={!foot} onClick={() => updateFoot(side, null)}>
                  <DeleteOutlineIcon sx={{ color: foot ? textColor : undefined }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <input
            ref={el => { fileInputRefs.current[side] = el; }}
            type="file"
            accept=".obj,.stl"
            hidden
            onChange={e => handleFileChange(side, e)}
          />
        </Box>
        <Typography variant="caption" sx={{ color: '#999', minHeight: '1.5em' }} noWrap>
          {foot ? foot.fileName : 'No foot loaded (optional)'}
        </Typography>

        <Box sx={{ position: 'relative', height: { xs: '35vh', md: '45vh' }, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
          <FootPointPickerScene
            geometry={foot?.geometry ?? null}
            points={foot?.points ?? []}
            onPointsChange={points => foot && updatePoints(side, foot, points)}
            isDarkMode={isDarkMode}
          />
          {!foot && (
            <Typography
              variant="body2"
              sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontStyle: 'italic', pointerEvents: 'none' }}
            >
              Import an OBJ or STL file
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Points: {foot?.points.length ?? 0} / {FOOT_POINTS_COUNT}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" disabled={!foot?.points.length} onClick={() => foot && updatePoints(side, foot, foot.points.slice(0, -1))}>
              Undo
            </Button>
            <Button size="small" disabled={!foot?.points.length} onClick={() => foot && updatePoints(side, foot, [])}>
              Clear
            </Button>
          </Box>
        </Box>
        {foot?.points.map((point, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: FOOT_POINT_COLORS[index] }} />
            <Typography variant="caption" sx={{ color: textColor, fontFamily: 'monospace' }}>
              {FOOT_POINT_LABELS[index]}: ({point.x.toFixed(2)}, {point.y.toFixed(2)}, {point.z.toFixed(2)})
            </Typography>
          </Box>
        ))}
        {foot && !foot.placed && foot.points.length < FOOT_POINTS_COUNT && (
          <Typography variant="caption" sx={{ color: FOOT_POINT_COLORS[foot.points.length], fontWeight: 600 }}>
            Next: click the {FOOT_POINT_LABELS[foot.points.length]}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="foot-mesh-title" closeAfterTransition sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          bgcolor: isDarkMode ? '#333' : '#fff',
          color: textColor,
          borderRadius: 2,
          width: { xs: '95vw', md: '80vw' },
          maxHeight: { xs: '95vh', md: '90vh' },
          mx: 'auto',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ccc' }}>
          <Typography id="foot-mesh-title" variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
            Import Foot Mesh
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: textColor }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', mb: 2 }}>
            Import a mesh for each foot (both are optional; a missing foot is mirrored from the other one). On the sole, click the heel first, then the 1st and 5th met heads (either order); drag to rotate, scroll to zoom.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {SIDES.map(renderFootSection)}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ color: textColor, borderColor: '#aaa', width: '80px', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              color: '#fff',
              backgroundColor: '#076596',
              width: '80px',
              fontWeight: 500,
              px: 4,
              '&:hover': { backgroundColor: '#0a6fb4' }
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FootMeshPanel;
